import type { BadgeTone } from '@/lib/utils/badgeTone'

import type {
  VenueCampaignCreateInput,
  VenueCampaignSendSlot,
  VenueCampaignStatus,
  VenueCampaignSummaryItem,
  VenueCampaignTargetInput,
  VenueCampaignType,
} from './campaignTypes'
import { EMPTY_MARK } from './format'
import { validateReservationPeriod, type ReservationPeriod } from './reservationViewModel'

/**
 * 공지 작성·이력 화면 공통 순수 함수(설계 §2) — 작성 폼 검증·대상 요약 문구·목록 필터·상태 톤·
 * 읽음률 표시 규칙. `closureViewModel.ts`와 같은 규칙 — `if (targetType === ...)`를 컴포넌트에
 * 흩지 않는다.
 */

const TITLE_MAX_LENGTH = 100
const BODY_MAX_LENGTH = 1000

export type ComposeTargetType = Extract<VenueCampaignTargetInput['targetType'], 'FOLLOWER' | 'RESERVATION'>
export type ComposeApplyScope = 'ALL_COURTS' | 'SELECTED_COURTS'

/** 작성 화면 폼 상태 — `POST campaigns`(#714 §1-1)의 부분집합(운영 공지는 `REGION`을 쓰지 않는다). */
export interface ComposeFormState {
  title: string
  body: string
  targetType: ComposeTargetType
  /** `RESERVATION` 전용, "yyyy-MM-dd". */
  reservationFromDate: string
  reservationToDate: string
  applyScope: ComposeApplyScope
  courtIds: number[]
}

export function initialComposeFormState(): ComposeFormState {
  return {
    title: '',
    body: '',
    targetType: 'FOLLOWER',
    reservationFromDate: '',
    reservationToDate: '',
    applyScope: 'ALL_COURTS',
    courtIds: [],
  }
}

export interface ComposeFormErrors {
  title?: string
  body?: string
  reservationFromDate?: string
  reservationToDate?: string
  courts?: string
}

export function hasComposeFormErrors(errors: ComposeFormErrors): boolean {
  return Object.values(errors).some((value) => Boolean(value))
}

/** #714 §1-1 규약 그대로 미러링한 클라이언트 사전 검증(최종 판정은 서버 `40050`). */
export function validateComposeForm(form: ComposeFormState): ComposeFormErrors {
  const errors: ComposeFormErrors = {}

  if (form.title.trim().length === 0) errors.title = '제목을 입력해 주세요.'
  else if (form.title.length > TITLE_MAX_LENGTH) errors.title = `제목은 ${TITLE_MAX_LENGTH}자 이내로 입력해 주세요.`

  if (form.body.trim().length === 0) errors.body = '내용을 입력해 주세요.'
  else if (form.body.length > BODY_MAX_LENGTH) errors.body = `내용은 ${BODY_MAX_LENGTH}자 이내로 입력해 주세요.`

  if (form.targetType === 'RESERVATION') {
    if (!form.reservationFromDate) errors.reservationFromDate = '예약 시작일을 선택해 주세요.'
    if (!form.reservationToDate) errors.reservationToDate = '예약 종료일을 선택해 주세요.'
    if (form.reservationFromDate && form.reservationToDate && form.reservationToDate < form.reservationFromDate) {
      errors.reservationToDate = '종료일은 시작일과 같거나 이후여야 합니다.'
    }
    if (form.applyScope === 'SELECTED_COURTS' && form.courtIds.length === 0) {
      errors.courts = '코트를 1개 이상 선택해 주세요.'
    }
  }

  return errors
}

export function toggleComposeCourt(form: ComposeFormState, courtId: number): ComposeFormState {
  const courtIds = form.courtIds.includes(courtId)
    ? form.courtIds.filter((id) => id !== courtId)
    : [...form.courtIds, courtId]
  return { ...form, courtIds }
}

/**
 * 대상 산출(`GET campaigns/estimate`) 쿼리 파라미터 — 대상 조건이 바뀔 때만 값이 바뀐다(제목·본문은
 * 포함하지 않는다, #714 §1-7이 요구하는 파라미터만 추린 것).
 */
export interface NoticeEstimateParams {
  type: 'NOTICE'
  targetType: ComposeTargetType
  reservationFromDate?: string
  reservationToDate?: string
  courtIds?: string
}

/** PROMOTION 견적 파라미터(#731 §2) — `promotionCampaignViewModel.ts`가 조립한다. */
export interface PromotionEstimateParams {
  type: 'PROMOTION'
  targetType: 'REGION'
  regionStateId: string
  regionCityId?: string
  sendSlot?: VenueCampaignSendSlot
}

/** #732 — PROMOTION이 처음 이 유니온에 합류한다. `fetchCampaignEstimate`는 타입 무관하게 그대로 재사용. */
export type CampaignEstimateParams = NoticeEstimateParams | PromotionEstimateParams

export function toEstimateParams(form: ComposeFormState): NoticeEstimateParams {
  if (form.targetType === 'FOLLOWER') {
    return { type: 'NOTICE', targetType: 'FOLLOWER' }
  }
  return {
    type: 'NOTICE',
    targetType: 'RESERVATION',
    reservationFromDate: form.reservationFromDate || undefined,
    reservationToDate: form.reservationToDate || undefined,
    courtIds: form.applyScope === 'SELECTED_COURTS' ? form.courtIds.join(',') : undefined,
  }
}

/** 대상 조건이 대상 산출을 재조회할 만큼 갖춰졌는가(빈 날짜로 400을 유발하지 않는다). */
export function estimateParamsReady(form: ComposeFormState): boolean {
  if (form.targetType === 'FOLLOWER') return true
  return Boolean(form.reservationFromDate) && Boolean(form.reservationToDate) && form.reservationToDate >= form.reservationFromDate
}

/** 폼 → `POST campaigns` 요청 본문. 🚨 `scheduledAt`은 항상 `null`(즉시 발송 전제, 설계 §자율 결정 3). */
export function toCampaignCreateInput(form: ComposeFormState): VenueCampaignCreateInput {
  return {
    type: 'NOTICE',
    title: form.title.trim(),
    body: form.body.trim(),
    imageUrl: null,
    linkUrl: null,
    target: {
      targetType: form.targetType,
      reservationFromDate: form.targetType === 'RESERVATION' ? form.reservationFromDate : null,
      reservationToDate: form.targetType === 'RESERVATION' ? form.reservationToDate : null,
      courtIds:
        form.targetType === 'RESERVATION' && form.applyScope === 'SELECTED_COURTS' ? form.courtIds : null,
      regionStateId: null,
      regionCityId: null,
      sendSlot: null,
    },
    scheduledAt: null,
  }
}

/** 발송 확인 모달·미리보기에 쓰는 사람이 읽는 대상 요약 문구. */
export function composeTargetSummaryText(
  form: ComposeFormState,
  courts: { courtId: number; name: string }[]
): string {
  if (form.targetType === 'FOLLOWER') return '팔로워 전체'

  const period =
    form.reservationFromDate && form.reservationToDate
      ? `${form.reservationFromDate} ~ ${form.reservationToDate}`
      : '기간 미지정'
  const courtText =
    form.applyScope === 'ALL_COURTS'
      ? '전체 코트'
      : form.courtIds.length > 0
        ? courts.filter((court) => form.courtIds.includes(court.courtId)).map((court) => court.name).join(', ')
        : '코트 미선택'
  return `특정 예약자 (${period}, ${courtText})`
}

/** 상세 화면의 대상 요약 문구 — 서버가 준 `target` 뷰(코드+이름 포함)를 그대로 사람이 읽는 문장으로. */
export function targetViewSummaryText(target: {
  targetType: VenueCampaignTargetInput['targetType']
  targetTypeLabel: string
  reservationFromDate: string | null
  reservationToDate: string | null
  courtIds: number[] | null
  regionStateName: string | null
  regionCityName: string | null
  sendSlotLabel: string | null
}): string {
  if (target.targetType === 'FOLLOWER') return target.targetTypeLabel

  if (target.targetType === 'RESERVATION') {
    const period =
      target.reservationFromDate && target.reservationToDate
        ? `${target.reservationFromDate} ~ ${target.reservationToDate}`
        : EMPTY_MARK
    const courtText = target.courtIds && target.courtIds.length > 0 ? `코트 ${target.courtIds.length}개` : '전체 코트'
    return `${target.targetTypeLabel} (${period}, ${courtText})`
  }

  // REGION(지역 홍보) — 이 화면은 쓰지 않지만 이력·상세는 조회하므로 방어적으로 조립한다.
  const region = [target.regionStateName, target.regionCityName].filter(Boolean).join(' ') || EMPTY_MARK
  const slot = target.sendSlotLabel ? `, ${target.sendSlotLabel}` : ''
  return `${target.targetTypeLabel} (${region}${slot})`
}

/** 읽음률·평균 읽음률 공통 표시 — 🚨 `null`을 `0.0%`로 그리지 않는다(#714/#728 반복 규약). */
export function readRateText(readRatePercent: number | null): string {
  return typeof readRatePercent === 'number' ? `${readRatePercent}%` : EMPTY_MARK
}

/** 대상 산출 건수 표시 — 🚨 `null`(집계 불가)을 `0명`으로 그리지 않는다(#714 §1-7). */
export function targetCountText(targetCount: number | null): string {
  return typeof targetCount === 'number' ? `${targetCount.toLocaleString('ko-KR')}명` : '집계 불가'
}

export const CAMPAIGN_STATUS_TONE: Record<VenueCampaignStatus, BadgeTone> = {
  DRAFT: 'neutral',
  SCHEDULED: 'warning',
  SENDING: 'warning',
  SENT: 'success',
  BLOCKED: 'danger',
  FAILED: 'danger',
}

/** 이력 목록 유형 필터 선택지(forward-only) — 실제 라벨은 항상 서버 `typeLabel`을 쓴다. */
export const CAMPAIGN_TYPE_FILTER_OPTIONS: { value: VenueCampaignType; label: string }[] = [
  { value: 'NOTICE', label: '운영 공지' },
  { value: 'PROMOTION', label: '지역 홍보' },
]

export interface CampaignListFilter extends ReservationPeriod {
  /** `undefined` = 전체 유형. */
  type?: VenueCampaignType
}

/** #728 §1 — 이력 목록·요약 모두 같은 62일 상한을 쓰므로 기존 예약 목록 검증 함수를 그대로 재사용한다. */
export const validateCampaignPeriod = validateReservationPeriod

/** 이력 목록 기본 조회 기간 — 최근 30일(오늘 포함). */
export function defaultCampaignListPeriod(today: string): ReservationPeriod {
  const fromMs = Date.parse(`${today}T00:00:00Z`) - 29 * 86_400_000
  const from = new Date(fromMs).toISOString().slice(0, 10)
  return { from, to: today }
}

/** 관리 홈 요약 기본 조회 기간 — 이번 달(1일 ~ 오늘). */
export function defaultCampaignSummaryPeriod(today: string): ReservationPeriod {
  return { from: `${today.slice(0, 7)}-01`, to: today }
}

/** 목록 API 쿼리 파라미터 — `undefined` 필드는 axios가 자동으로 뺀다. */
export function campaignListQueryParams(
  filter: CampaignListFilter,
  cursor?: string
): Record<string, string | undefined> {
  return {
    type: filter.type,
    from: filter.from,
    to: filter.to,
    cursor,
  }
}

export type RecipientReadFilter = 'ALL' | 'READ' | 'UNREAD'

export const RECIPIENT_READ_FILTER_OPTIONS: { value: RecipientReadFilter; label: string }[] = [
  { value: 'ALL', label: '전체' },
  { value: 'READ', label: '읽음' },
  { value: 'UNREAD', label: '안읽음' },
]

/** #728 §6 — `read` 파라미터: 생략(전체) / `true`(읽은 사람만) / `false`(안 읽은 사람만). */
export function recipientReadParam(filter: RecipientReadFilter): boolean | undefined {
  if (filter === 'READ') return true
  if (filter === 'UNREAD') return false
  return undefined
}

/**
 * 이력 목록 행 클릭 목적지(#732 설계 §자율 결정 6) — `venues/{venueId}/` 접두사는 호출부가 붙인다.
 * 🚨 `PROMOTION`+`DRAFT`만 "이어서 작성" 편집 경로로 보낸다. 그 외(모든 `NOTICE`, `SENT`·`SENDING`·
 * `BLOCKED`·`FAILED`·`SCHEDULED` `PROMOTION`)는 기존 읽기 전용 상세로 보낸다 — `SCHEDULED`·`FAILED`
 * 재편집·재예약 UI는 이번 태스크 범위 밖이다(자율 결정 6).
 */
export function campaignRowPath(item: Pick<VenueCampaignSummaryItem, 'id' | 'type' | 'status'>): string {
  if (item.type === 'PROMOTION' && item.status === 'DRAFT') {
    return `notices/promotions/${item.id}/edit`
  }
  return `notices/${item.id}`
}
